/**
 * Content-language model for the blog. Ghost has no native i18n, so editors
 * declare a post's language with an INTERNAL tag and (optionally) link
 * translations of one article with a second internal tag:
 *
 *   #lang-pt-pt  (Ghost slug `hash-lang-pt-pt`)  language = LOCALE_META slug
 *   #t-launch    (Ghost slug `hash-t-launch`)    translation group "launch"
 *
 * A post with no `#lang-*` tag is English. See README "Blog languages".
 */
import {
  DEFAULT_LOCALE,
  LOCALE_META,
  LOCALES,
  type Locale,
} from "@thyra/landing-i18n";

export const LANG_TAG_PREFIX = "hash-lang-";
export const TRANSLATION_TAG_PREFIX = "hash-t-";

/** Locales that need an explicit `#lang-*` tag (everything but English). */
export const TAGGED_LOCALES: readonly Locale[] = LOCALES.filter(
  (l) => l !== DEFAULT_LOCALE,
);

/** Ghost slug of the internal tag that marks a post as written in `locale`. */
export function langTagSlug(locale: Locale): string {
  return `${LANG_TAG_PREFIX}${LOCALE_META[locale].slug}`;
}

/** Ghost slug of the internal tag that groups translations of one article. */
export function translationTagSlug(key: string): string {
  return `${TRANSLATION_TAG_PREFIX}${key}`;
}

/** Language of a post from its tag slugs (internal ones included); default English. */
export function langFromTagSlugs(slugs: readonly string[]): Locale {
  for (const locale of TAGGED_LOCALES) {
    if (slugs.includes(langTagSlug(locale))) return locale;
  }
  return DEFAULT_LOCALE;
}

const TRANSLATION_KEY = /^[a-z0-9][a-z0-9-]*$/;

/** `#t-<key>` group key from tag slugs, or null when the post has none. */
export function translationKeyFromTagSlugs(
  slugs: readonly string[],
): string | null {
  for (const slug of slugs) {
    if (!slug.startsWith(TRANSLATION_TAG_PREFIX)) continue;
    const key = slug.slice(TRANSLATION_TAG_PREFIX.length);
    if (TRANSLATION_KEY.test(key)) return key;
  }
  return null;
}

export function isTranslationKey(key: string): boolean {
  return TRANSLATION_KEY.test(key);
}

export type TranslationLink = { lang: Locale; slug: string };

/** Posts grouped by language, newest first as given. */
export function groupByLang<T extends { lang: Locale }>(
  posts: readonly T[],
): Map<Locale, T[]> {
  const out = new Map<Locale, T[]>(LOCALES.map((l) => [l, []]));
  for (const post of posts) out.get(post.lang)?.push(post);
  return out;
}

/**
 * Translation groups: `key -> one entry per language` (the first, i.e. newest,
 * post wins when a language appears twice). Groups of one are dropped: a
 * lone post has no alternates.
 */
export function translationGroups<
  T extends { slug: string; lang: Locale; translationKey: string | null },
>(posts: readonly T[]): Map<string, TranslationLink[]> {
  const groups = new Map<string, TranslationLink[]>();
  for (const post of posts) {
    if (!post.translationKey) continue;
    const group = groups.get(post.translationKey) ?? [];
    if (!group.some((m) => m.lang === post.lang)) {
      group.push({ lang: post.lang, slug: post.slug });
    }
    groups.set(post.translationKey, group);
  }
  for (const [key, group] of groups) if (group.length < 2) groups.delete(key);
  return groups;
}
